import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Clock, User, AlertCircle, ChevronLeft, ChevronRight, Shield, Lock, Award, X, Grid } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { useAntiCheat } from '@/hooks/use-anti-cheat'
import { AnimatePresence, motion } from 'framer-motion'

interface Question {
    id: number
    section: string
    subsection: string
    question: string
    options: string[]
    correctAnswer: number
}

type QuestionStatus = 'not-visited' | 'not-answered' | 'answered' | 'marked' | 'answered-marked'

export default function TestExamPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const { candidateName, rollNumber } = location.state || {}

    const [currentSection, setCurrentSection] = useState('Physics')
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
    const [answers, setAnswers] = useState<Map<number, number>>(new Map())
    const [questionStatuses, setQuestionStatuses] = useState<Map<number, QuestionStatus>>(new Map())
    const [markedForReview, setMarkedForReview] = useState<Set<number>>(new Set())
    const [timeRemaining, setTimeRemaining] = useState(200 * 60) // 200 minutes
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
    const [isPaletteOpen, setIsPaletteOpen] = useState(false) // Mobile drawer state

    // New States for requested features
    const [showPromoModal, setShowPromoModal] = useState(false)
    const [violationModal, setViolationModal] = useState<{ show: boolean, message: string }>({ show: false, message: '' })

    // Anti-cheat system
    const antiCheat = useAntiCheat({
        maxStrikes: 5,
        onMaxStrikes: () => {
            setViolationModal({
                show: true,
                message: 'Maximum violations reached! The test will be submitted automatically.'
            })
            setTimeout(() => {
                setViolationModal({ show: false, message: '' })
                handleAutoSubmit()
            }, 3000)
        },
        onViolation: (violation) => {
            setViolationModal({
                show: true,
                message: `Warning: ${violation}! Strike ${antiCheat.strikes + 1}/5`
            })
        },
    })

    // Mock questions data
    const questions: Question[] = Array.from({ length: 200 }, (_, i) => ({
        id: i + 1,
        section: i < 50 ? 'Physics' : i < 100 ? 'Chemistry' : 'Biology',
        subsection: (i % 50) < 35 ? 'Section A' : 'Section B',
        question: `Question ${i + 1}: This is a sample question for ${i < 50 ? 'Physics' : i < 100 ? 'Chemistry' : 'Biology'}. In a real test, this would contain the actual question text with proper formatting, images, and mathematical equations if needed.`,
        options: [
            `Option A for question ${i + 1}`,
            `Option B for question ${i + 1}`,
            `Option C for question ${i + 1}`,
            `Option D for question ${i + 1}`,
        ],
        correctAnswer: Math.floor(Math.random() * 4),
    }))

    const sectionQuestions = questions.filter(q => q.section === currentSection)
    const currentQuestion = sectionQuestions[currentQuestionIndex]

    // Timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    handleAutoSubmit()
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [])

    // Update question status when visiting
    useEffect(() => {
        if (currentQuestion && !questionStatuses.has(currentQuestion.id)) {
            setQuestionStatuses(prev => new Map(prev).set(currentQuestion.id, 'not-answered'))
        }
    }, [currentQuestion])

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = seconds % 60
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const handleSaveAndNext = () => {
        if (selectedAnswer !== null) {
            setAnswers(prev => new Map(prev).set(currentQuestion.id, selectedAnswer))
            setQuestionStatuses(prev => {
                const newMap = new Map(prev)
                if (markedForReview.has(currentQuestion.id)) {
                    newMap.set(currentQuestion.id, 'answered-marked')
                } else {
                    newMap.set(currentQuestion.id, 'answered')
                }
                return newMap
            })
        }
        goToNextQuestion()
    }

    const handleMarkForReview = () => {
        setMarkedForReview(prev => {
            const newSet = new Set(prev)
            newSet.add(currentQuestion.id)
            return newSet
        })
        if (selectedAnswer !== null) {
            setAnswers(prev => new Map(prev).set(currentQuestion.id, selectedAnswer))
            setQuestionStatuses(prev => new Map(prev).set(currentQuestion.id, 'answered-marked'))
        } else {
            setQuestionStatuses(prev => new Map(prev).set(currentQuestion.id, 'marked'))
        }
        goToNextQuestion()
    }

    const handleClearResponse = () => {
        setSelectedAnswer(null)
        setAnswers(prev => {
            const newMap = new Map(prev)
            newMap.delete(currentQuestion.id)
            return newMap
        })
        setQuestionStatuses(prev => {
            const newMap = new Map(prev)
            if (markedForReview.has(currentQuestion.id)) {
                newMap.set(currentQuestion.id, 'marked')
            } else {
                newMap.set(currentQuestion.id, 'not-answered')
            }
            return newMap
        })
    }

    const goToNextQuestion = () => {
        if (currentQuestionIndex < sectionQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1)
            loadQuestionAnswer(sectionQuestions[currentQuestionIndex + 1].id)
        }
    }

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1)
            loadQuestionAnswer(sectionQuestions[currentQuestionIndex - 1].id)
        }
    }

    const loadQuestionAnswer = (questionId: number) => {
        setSelectedAnswer(answers.get(questionId) ?? null)
    }

    const handleQuestionClick = (index: number) => {
        setCurrentQuestionIndex(index)
        loadQuestionAnswer(sectionQuestions[index].id)
        if (window.innerWidth < 1024) setIsPaletteOpen(false) // Close drawer on mobile selection
    }

    const handleSectionChange = (section: string) => {
        setCurrentSection(section)
        setCurrentQuestionIndex(0)
        const firstQuestion = questions.find(q => q.section === section)
        if (firstQuestion) {
            loadQuestionAnswer(firstQuestion.id)
        }
    }

    const getQuestionStatus = (questionId: number): QuestionStatus => {
        return questionStatuses.get(questionId) || 'not-visited'
    }

    const getStatusColor = (status: QuestionStatus) => {
        switch (status) {
            case 'answered': return 'bg-green-500'
            case 'not-answered': return 'bg-red-500'
            case 'marked': return 'bg-purple-500'
            case 'answered-marked': return 'bg-purple-500'
            case 'not-visited': return 'bg-slate-600'
            default: return 'bg-slate-600'
        }
    }

    const handleAutoSubmit = () => {
        handleSubmitTest()
    }

    const handleSubmitTest = () => {
        setShowSubmitConfirm(false)
        setShowPromoModal(true)
    }

    const proceedToResults = () => {
        const totalAnswered = answers.size
        navigate('/test/results', {
            state: {
                candidateName,
                rollNumber,
                totalQuestions: questions.length,
                answered: totalAnswered,
                notAnswered: questions.length - totalAnswered,
                markedForReview: markedForReview.size,
                // Pass full data for detailed report
                questions: questions,
                userAnswers: Array.from(answers.entries()), // Convert Map to array for serialization
            }
        })
    }

    const getStatusCounts = () => {
        const counts = {
            answered: 0,
            notAnswered: 0,
            marked: 0,
            notVisited: 0,
        }

        sectionQuestions.forEach(q => {
            const status = getQuestionStatus(q.id)
            switch (status) {
                case 'answered':
                case 'answered-marked':
                    counts.answered++
                    break
                case 'not-answered':
                    counts.notAnswered++
                    break
                case 'marked':
                    counts.marked++
                    break
                case 'not-visited':
                    counts.notVisited++
                    break
            }
        })

        return counts
    }

    const statusCounts = getStatusCounts()

    return (
        <div className="h-screen w-full flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden">
            {/* Header */}
            <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-3 px-4 shrink-0 z-20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {/* Mobile: Toggle Palette */}
                        <button
                            onClick={() => setIsPaletteOpen(!isPaletteOpen)}
                            className="lg:hidden p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-theme-primary"
                        >
                            <Grid className="h-5 w-5" />
                        </button>

                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary-blue/20 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary-blue" />
                            </div>
                            <div className="hidden md:block">
                                <p className="text-theme-primary font-semibold text-sm">{candidateName || 'Candidate'}</p>
                                <p className="text-theme-muted text-xs">Roll No: {rollNumber || '123456'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center gap-6">
                        {/* Anti-Cheat Strike Counter - Desktop */}
                        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                            <Shield className="h-5 w-5 text-primary-blue" />
                            <span className="text-theme-primary font-semibold">Strikes:</span>
                            <span className={`font-bold ${antiCheat.strikes === 0 ? 'text-green-500' :
                                antiCheat.strikes < 3 ? 'text-yellow-500' :
                                    antiCheat.strikes < 5 ? 'text-orange-500' :
                                        'text-red-500'
                                }`}>
                                {antiCheat.strikes}/5
                            </span>
                        </div>
                    </div>

                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeRemaining < 300 ? 'bg-red-500/10 border border-red-500' : 'bg-primary-blue/10 border border-primary-blue'}`}>
                        <Clock className={`h-5 w-5 ${timeRemaining < 300 ? 'text-red-500' : 'text-primary-blue'}`} />
                        <span className={`font-mono font-bold ${timeRemaining < 300 ? 'text-red-500' : 'text-theme-primary'}`}>
                            {formatTime(timeRemaining)}
                        </span>
                    </div>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden relative">
                {/* Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden w-full">
                    {/* Section Tabs */}
                    <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 shrink-0 overflow-x-auto">
                        <div className="flex gap-2">
                            {['Physics', 'Chemistry', 'Biology'].map(section => (
                                <button
                                    key={section}
                                    onClick={() => handleSectionChange(section)}
                                    className={`px-4 lg:px-6 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${currentSection === section
                                        ? 'bg-primary-blue text-white'
                                        : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                        }`}
                                >
                                    {section}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Question Content */}
                    <div className="flex-1 overflow-y-auto p-4 lg:p-6 pb-32 lg:pb-6 custom-scrollbar">
                        <div className="max-w-4xl mx-auto">
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg lg:text-xl font-semibold text-theme-primary">
                                        Question {currentQuestionIndex + 1} of {sectionQuestions.length}
                                    </h2>
                                    <span className="text-xs lg:text-sm text-theme-muted">{currentQuestion?.subsection}</span>
                                </div>
                                <div className="p-4 lg:p-6 bg-white dark:bg-slate-900/60 rounded-lg border border-slate-200 dark:border-slate-700/50 shadow-sm">
                                    <p className="text-base lg:text-lg text-theme-primary leading-relaxed">{currentQuestion?.question}</p>
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-3">
                                {currentQuestion?.options.map((option, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedAnswer(index)}
                                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${selectedAnswer === index
                                            ? 'border-primary-blue bg-primary-blue/10 text-theme-primary'
                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 text-theme-secondary hover:border-slate-300 dark:hover:border-slate-600'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedAnswer === index
                                                ? 'border-primary-blue bg-primary-blue'
                                                : 'border-slate-400 dark:border-slate-600'
                                                }`}>
                                                {selectedAnswer === index && (
                                                    <div className="w-3 h-3 rounded-full bg-white" />
                                                )}
                                            </div>
                                            <span className="font-semibold mr-2 text-sm lg:text-base">{String.fromCharCode(65 + index)}.</span>
                                            <span className="text-sm lg:text-base">{option}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons - Fixed Bottom */}
                    <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-3 md:px-4 py-3 shrink-0">
                        <div className="max-w-4xl mx-auto flex flex-col lg:flex-row gap-2.5 lg:gap-3 lg:items-center justify-between">
                            {/* Mobile: Stacked Actions with larger touch targets */}
                            <div className="flex gap-2 lg:hidden">
                                <button
                                    onClick={handleMarkForReview}
                                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg active:scale-95 transition-all min-h-[44px]"
                                >
                                    Mark Review
                                </button>
                                <button
                                    onClick={handleClearResponse}
                                    className="flex-1 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white text-sm font-semibold rounded-lg active:scale-95 transition-all min-h-[44px]"
                                >
                                    Clear
                                </button>
                            </div>

                            <div className="flex gap-2 justify-between w-full lg:w-auto">
                                <button
                                    onClick={goToPreviousQuestion}
                                    disabled={currentQuestionIndex === 0}
                                    className="flex-1 lg:flex-none px-5 md:px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm min-h-[44px] active:scale-95"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    <span className="hidden xs:inline">Prev</span>
                                </button>

                                <div className="hidden lg:flex gap-3">
                                    <button
                                        onClick={handleMarkForReview}
                                        className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                                    >
                                        Mark Review
                                    </button>
                                    <button
                                        onClick={handleClearResponse}
                                        className="px-6 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>

                                <button
                                    onClick={handleSaveAndNext}
                                    className="flex-1 lg:flex-none px-5 md:px-6 py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 text-sm min-h-[44px] active:scale-95"
                                >
                                    <span className="hidden xs:inline">Next</span>
                                    <span className="inline xs:hidden">→</span>
                                    <ChevronRight className="h-4 w-4 hidden xs:inline" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Sidebar - Question Palette (Responsive Drawer) */}
                <AnimatePresence>
                    {(isPaletteOpen || window.innerWidth >= 1024) && (
                        <>
                            {/* Backdrop for mobile */}
                            {isPaletteOpen && (
                                <motion.div
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                                    onClick={() => setIsPaletteOpen(false)}
                                />
                            )}

                            <motion.div
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{ type: 'tween', duration: 0.3 }}
                                className={`
                                    fixed lg:static inset-y-0 right-0 z-40 w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 flex flex-col shadow-2xl lg:shadow-none
                                    ${window.innerWidth >= 1024 ? '!transform-none' : ''} 
                                `}
                            >
                                <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                                    <h3 className="font-semibold text-theme-primary">Question Palette</h3>
                                    <button onClick={() => setIsPaletteOpen(false)} className="lg:hidden p-1 hover:bg-slate-100 rounded">
                                        <X className="h-5 w-5 text-theme-secondary" />
                                    </button>
                                </div>

                                {/* Status Legend */}
                                <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded bg-green-500 flex items-center justify-center text-white font-bold text-[10px]">
                                                {statusCounts.answered}
                                            </div>
                                            <span className="text-theme-secondary">Answered</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center text-white font-bold text-[10px]">
                                                {statusCounts.notAnswered}
                                            </div>
                                            <span className="text-theme-secondary">Not Ans</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded bg-purple-500 flex items-center justify-center text-white font-bold text-[10px]">
                                                {statusCounts.marked}
                                            </div>
                                            <span className="text-theme-secondary">Review</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-slate-700 dark:text-white font-bold text-[10px]">
                                                {statusCounts.notVisited}
                                            </div>
                                            <span className="text-theme-secondary">Not Visited</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Question Grid */}
                                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                    <div className="grid grid-cols-5 gap-2">
                                        {sectionQuestions.map((q, index) => {
                                            const status = getQuestionStatus(q.id)
                                            return (
                                                <button
                                                    key={q.id}
                                                    onClick={() => handleQuestionClick(index)}
                                                    className={`aspect-square rounded text-xs lg:text-sm font-semibold hover:opacity-80 transition-opacity ${getStatusColor(status)} text-white ${currentQuestionIndex === index ? 'ring-2 ring-offset-2 ring-primary-blue dark:ring-offset-slate-900' : ''
                                                        }`}
                                                >
                                                    {index + 1}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                                    <button
                                        onClick={() => setShowSubmitConfirm(true)}
                                        className="w-full px-6 py-3 bg-accent-gold hover:bg-yellow-600 text-slate-900 font-bold rounded-lg transition-colors"
                                    >
                                        Submit Test
                                    </button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>

            {/* Submit Confirmation Modal */}
            <AnimatePresence>
                {showSubmitConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
                    >
                        <GlassmorphicCard variant="elevated" className="max-w-md w-full p-8 relative">
                            <button onClick={() => setShowSubmitConfirm(false)} className="absolute top-4 right-4 text-theme-secondary">
                                <X className="h-6 w-6" />
                            </button>
                            <div className="flex items-start gap-4 mb-6">
                                <AlertCircle className="h-8 w-8 text-accent-gold flex-shrink-0" />
                                <div>
                                    <h3 className="text-2xl font-display font-bold text-theme-primary mb-2">
                                        Submit Test?
                                    </h3>
                                    <p className="text-theme-secondary">
                                        Are you sure you want to submit? This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-slate-100 dark:bg-slate-900/60 rounded-lg p-4 mb-6">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-theme-muted">Answered</p>
                                        <p className="text-theme-primary font-bold text-lg">{answers.size}</p>
                                    </div>
                                    <div>
                                        <p className="text-theme-muted">Not Answered</p>
                                        <p className="text-theme-primary font-bold text-lg">{questions.length - answers.size}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowSubmitConfirm(false)}
                                    className="flex-1 px-6 py-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-semibold rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitTest}
                                    className="flex-1 px-6 py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
                                >
                                    Yes, Submit
                                </button>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Violation Modal */}
            <AnimatePresence>
                {violationModal.show && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] p-4"
                    >
                        <GlassmorphicCard variant="elevated" className="max-w-md w-full p-8 border-red-500/50">
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                                    <Lock className="h-8 w-8 text-red-500" />
                                </div>
                                <h3 className="text-2xl font-display font-bold text-red-500 mb-2">
                                    Security Violation Detected
                                </h3>
                                <p className="text-theme-secondary text-lg mb-6">
                                    {violationModal.message}
                                </p>
                                <button
                                    onClick={() => setViolationModal({ show: false, message: '' })}
                                    className="px-8 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
                                >
                                    I Understand
                                </button>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Promotional Promo Modal */}
            <AnimatePresence>
                {showPromoModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 bg-black/95 flex items-center justify-center z-[80] p-4"
                    >
                        <GlassmorphicCard variant="elevated" className="max-w-2xl w-full p-0 overflow-hidden">
                            <div className="relative h-48 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center overflow-hidden">
                                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80')] bg-cover bg-center opacity-20" />
                                <div className="relative z-10 text-center p-6">
                                    <Award className="h-12 w-12 text-yellow-300 mx-auto mb-4" />
                                    <h2 className="text-3xl font-bold text-white mb-2">Test Completed Successfully!</h2>
                                    <p className="text-blue-100">Great effort! Take your preparation to the next level.</p>
                                </div>
                            </div>

                            <div className="p-8 bg-white dark:bg-slate-900">
                                <div className="flex flex-col md:flex-row gap-8 items-center">
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-theme-primary mb-4">Unlock Premium Courses</h3>
                                        <ul className="space-y-3 mb-6">
                                            {[
                                                'Personalized Doubt Solving',
                                                '1-on-1 Mentorship',
                                                'Advanced Test Analytics',
                                                'Complete Video Solutions'
                                            ].map((feature, i) => (
                                                <li key={i} className="flex items-center gap-2 text-theme-secondary">
                                                    <div className="w-5 h-5 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center text-xs">✓</div>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex-1 flex flex-col gap-3 w-full">
                                        <button
                                            onClick={proceedToResults}
                                            className="py-4 px-6 rounded-xl bg-gradient-to-r from-primary-blue to-primary-purple text-white font-semibold hover:shadow-lg transition-all transform hover:scale-[1.02]"
                                        >
                                            View Test Results
                                        </button>
                                        <button
                                            onClick={() => navigate('/courses')}
                                            className="py-4 px-6 rounded-xl bg-white dark:bg-slate-800 border-2 border-primary-blue text-primary-blue font-semibold hover:bg-primary-blue hover:text-white transition-all"
                                        >
                                            Explore Courses
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
